import { isNil, merge, omitBy } from 'lodash';
import { action, makeAutoObservable } from 'mobx';
import type { Order } from 'models/order/order';
import type { OrderActionInfo } from 'models/order/orderActionInfo';
import type { OrderPaymentInfo } from 'models/order/orderPaymentInfo';
import type { OrderProject, ProjectProduct } from 'models/order/orderProject';

export default class OrderPageStore {
  order: Order | undefined;

  actions: OrderActionInfo[] | undefined;
  payments: OrderPaymentInfo[] | undefined;
  projects: OrderProject[] | undefined;

  projectChanges = { toAdd: new Set<number>([]), toEdit: new Set<number>([]) };

  constructor() {
    makeAutoObservable(this);
  }

  updateOrder = action((order: Order): void => {
    this.order = order;
  });

  //  ┌─────────────────────────────────── ∘°❉°∘ ───────────────────────────────────┐
  //  │                                   PAYMENTS                                  │
  //  └─────────────────────────────────── °∘❉∘° ───────────────────────────────────┘

  updatePayments = action((newPayments: OrderPaymentInfo[]): void => {
    this.payments = newPayments;
  });

  //  ┌─────────────────────────────────── ∘°❉°∘ ───────────────────────────────────┐
  //  │                                   ACTIONS                                   │
  //  └─────────────────────────────────── °∘❉∘° ───────────────────────────────────┘

  updateActions = action((newActions: OrderActionInfo[]): void => {
    this.actions = newActions;
  });

  //  ┌─────────────────────────────────── ∘°❉°∘ ───────────────────────────────────┐
  //  │                                   PROJECTS                                  │
  //  └─────────────────────────────────── °∘❉∘° ───────────────────────────────────┘

  updateProjects = action((newProjects: OrderProject[]): void => {
    this.projects = newProjects;
    this.projectChanges.toEdit.clear();
    this.projectChanges.toAdd.clear();
  });

  updateProject = action(
    (
      projectId: number,
      data: Partial<Pick<OrderProject, 'name' | 'active' | 'orderProducts'>>
    ): OrderProject | undefined => {
      const project = this.projects?.find((p) => p.id === projectId);

      if (isNil(project)) return;

      merge(project, omitBy(data, isNil));

      if (project.id > 0) {
        this.projectChanges.toEdit.add(project.id);
      }

      return project;
    }
  );

  addProject = action((project: OrderProject): OrderProject[] | undefined => {
    this.projects?.unshift(project);

    this.projectChanges.toAdd.add(project.id);

    return this.projects;
  });

  removeProduct = action(
    (projectId: number, productId: number): ProjectProduct[] | undefined =>
      this.replaceProduct(projectId, productId)
  );

  addProducts = action(
    (projectId: number, newProducts: ProjectProduct[]): ProjectProduct[] | undefined => {
      const project = this.projects?.find((p) => p.id === projectId);

      if (isNil(project)) return;

      project.orderProducts = [...project.orderProducts, ...newProducts];

      if (project.id > 0) {
        this.projectChanges.toEdit.add(project.id);
      }

      return project.orderProducts;
    }
  );

  replaceProduct = action(
    (
      projectId: number,
      productId: number,
      newProduct?: ProjectProduct
    ): ProjectProduct[] | undefined => {
      const project = this.projects?.find((p) => p.id === projectId);

      if (isNil(project)) return;

      const productIndex = project.orderProducts.findIndex((p) => p.id === productId);

      if (productIndex < 0) return;

      project.orderProducts = project.orderProducts.filter((_, i) => i !== productIndex);

      if (!isNil(newProduct)) {
        project.orderProducts = [
          ...project.orderProducts.slice(0, productIndex),
          newProduct,
          ...project.orderProducts.slice(productIndex),
        ];
      }

      if (project.id > 0) {
        this.projectChanges.toEdit.add(project.id);
      }

      return project?.orderProducts;
    }
  );
}
